import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { ScaReason, ScaService } from '@platform-frontend/marketplace';
import { PollingConfiguration } from '@platform-frontend/platform';
import { PopupFeatures, PopupService } from '@platform-frontend/ui-tools';
import { Navigate, tapNextOrError } from '@platform-frontend/util';
import { tap } from 'rxjs/operators';
import { ScaUrlService } from '../shared/sca-url.service';

@UntilDestroy()
@Component({
  selector: 'yat-sca-spinner',
  templateUrl: './sca-spinner.component.html',
  styleUrls: ['./sca-spinner.component.scss']
})
export class ScaSpinnerComponent implements OnInit, OnDestroy {
  constructor(
    private popupService: PopupService,
    private route: ActivatedRoute,
    private scaService: ScaService,
    private scaUrlService: ScaUrlService,
    private store: Store
  ) {}

  private readonly SCA_POPUP = 'sca-polling';

  pollingConfiguration: PollingConfiguration;

  reason: ScaReason;

  ngOnInit(): void {
    this.initializePopup();
    this.pollingConfiguration = this.scaService.getPollingConfiguration();
    this.reason = this.scaService.getReason();
    this.navigateAfterSca();
  }

  private navigateAfterSca(): void {
    const navExtras = { clearNotifications: false, relativeTo: this.route };
    this.scaService
      .getResult()
      .pipe(
        // Closing the popup on component destroy does not work as expected in a mobile app context. That's why we also close the popup here.
        tapNextOrError(() => this.popupService.close(this.SCA_POPUP)),
        tap({
          next: () => this.openSuccessUrl(this.reason, navExtras),
          error: () => this.store.dispatch(new Navigate([this.scaUrlService.getFailureUrl(this.reason)], navExtras))
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private openSuccessUrl(reason: ScaReason, navExtras: NavigationExtras): void {
    const successUrl = this.scaUrlService.getSuccessUrl(reason);
    this.store.dispatch(new Navigate([successUrl.url], { ...navExtras, queryParams: successUrl.queryParams }));
  }

  private initializePopup(): void {
    const features = new PopupFeatures(460, 730);
    this.popupService.openOrNavigate(this.SCA_POPUP, this.scaService.getUrl(), features, true);
    this.popupService
      .closedByUser(this.SCA_POPUP)
      .pipe(
        tap(() => this.cancel()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  cancel(): void {
    this.scaService.cancel();
  }

  @HostListener('window:popstate')
  onBackNavigation(): void {
    this.scaService.cancel();
  }

  ngOnDestroy(): void {
    this.popupService.close(this.SCA_POPUP);
  }
}
